import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import WorkspaceSidebar from "./WorkspaceSidebar";
import "./WorkspaceLayout.css";
import { apiRequest } from "../helpers/api";
import { AccessProvider } from "./AccessContext";
import { WorkspaceRouteGuard } from "./WorkspaceAccess";

const routeTitles = {
  "/dashboard": "Dashboard",
  "/content-calendar": "Content Calendar",
  "/ai-post-studio": "AI Post Studio",
  "/ai-post-studio/create": "Create AI Post",
  "/manual-post-studio": "Manual Post Studio",
  "/manual-post-studio/create": "Create Post",
  "/media-library": "Media Library",
  "/social-accounts": "Social Accounts",
  "/messages": "Messages / DMs",
  "/leads": "Leads",
  "/analytics": "Analytics",
  "/business-profile": "Business Profile",
  "/products-services": "Products & Services",
  "/brand-settings": "Brand Settings",
  "/content-preferences": "Content Preferences",
  "/notifications": "Notifications",
  "/help-support": "Help & Support",
  "/settings": "Settings",
  "/upgrade-plan": "Upgrade Plan",
  "/account/profile": "User Profile",
};

const FoundationLayout = () => <AccessProvider><FoundationLayoutFrame /></AccessProvider>;

const FoundationLayoutFrame = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("workspaceSidebarCollapsed") === "true");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 760px)").matches);
  const [unreadCount, setUnreadCount] = useState(0);
  const [planLimit, setPlanLimit] = useState(null);

  useEffect(() => {
    const showPlanLimit = (event) => setPlanLimit(event.detail || null);
    window.addEventListener("plan:limit", showPlanLimit);
    return () => window.removeEventListener("plan:limit", showPlanLimit);
  }, []);

  useEffect(() => {
    if (location.pathname === "/upgrade-plan") setPlanLimit(null);
  }, [location.pathname]);

  useEffect(() => {
    let active = true;
    const refreshCount = async () => {
      try {
        const result = await apiRequest("/api/notifications/count");
        if (active) setUnreadCount(result.unreadCount || 0);
      } catch (error) {
        if (error.status !== 401) console.error("Unable to refresh notification count:", error.message);
      }
    };
    refreshCount();
    const timer = window.setInterval(refreshCount, 30000);
    const onRefresh = () => refreshCount();
    window.addEventListener("notifications:refresh", onRefresh);
    window.addEventListener("focus", onRefresh);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener("notifications:refresh", onRefresh);
      window.removeEventListener("focus", onRefresh);
    };
  }, []);

  useEffect(() => {
    const mobileQuery = window.matchMedia("(max-width: 760px)");
    const updateMobileMode = () => {
      const mobile = mobileQuery.matches;
      setIsMobile(mobile);
      setMobileOpen(false);
    };
    mobileQuery.addEventListener("change", updateMobileMode);
    window.addEventListener("resize", updateMobileMode);
    return () => {
      mobileQuery.removeEventListener("change", updateMobileMode);
      window.removeEventListener("resize", updateMobileMode);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("workspaceSidebarCollapsed", String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleOnboardingRequired = () => navigate("/onboarding", { replace: true });
    window.addEventListener("auth:onboarding-required", handleOnboardingRequired);
    return () => window.removeEventListener("auth:onboarding-required", handleOnboardingRequired);
  }, [navigate]);

  useEffect(() => {
    const handleExpiredSession = () => navigate("/login", { replace: true, state: { from: location } });
    window.addEventListener("auth:expired", handleExpiredSession);
    return () => window.removeEventListener("auth:expired", handleExpiredSession);
  }, [location, navigate]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [mobileOpen]);

  const editingStudio = location.pathname.match(/^\/(ai|manual)-post-studio\/[^/]+\/edit$/);
  const currentTitle = routeTitles[location.pathname] || (editingStudio ? (editingStudio[1] === "ai" ? "Edit AI Post" : "Edit Post") : "Socialyorix");
  const sidebarCollapsed = collapsed && !isMobile;

  return (
    <div data-bs-theme="light" className={`workspace-shell${sidebarCollapsed ? " is-collapsed" : ""}${isMobile ? " is-mobile" : ""}${mobileOpen ? " mobile-open" : ""}`}>
      {mobileOpen && <button className="workspace-drawer-scrim" type="button" aria-label="Close navigation" onClick={() => setMobileOpen(false)} />}
      <WorkspaceSidebar
        collapsed={sidebarCollapsed}
        unreadCount={unreadCount}
        onToggleCollapsed={() => setCollapsed((value) => !value)}
        onNavigate={() => setMobileOpen(false)}
      />
      <div className="workspace-frame">
        <header className="workspace-topbar">
          <div className="workspace-topbar-start">
            <button className="workspace-menu-button" type="button" aria-label="Open navigation" aria-expanded={mobileOpen} aria-controls="workspace-sidebar" onClick={() => setMobileOpen(true)}>
              <i className="ti ti-menu-2" aria-hidden="true" />
            </button>
            <div className="workspace-breadcrumb"><strong>{currentTitle}</strong></div>
          </div>
          <div className="workspace-topbar-actions">
            <button
              className="workspace-notification-button"
              type="button"
              onClick={() => navigate("/notifications")}
              aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ""}`}
              title="Notifications"
            >
              <i className="ti ti-bell" aria-hidden="true" />
              {unreadCount > 0 && <span className="workspace-notification-count">{unreadCount > 99 ? "99+" : unreadCount}</span>}
            </button>
          </div>
        </header>
        {planLimit && (
          <div className="workspace-plan-limit" role="status">
            <i className={`ti ${planLimit.code === "PLAN_FEATURE_UNAVAILABLE" ? "ti-lock" : "ti-gauge"}`} aria-hidden="true" />
            <span><strong>{planLimit.code === "PLAN_FEATURE_UNAVAILABLE" ? "Not included in your plan." : "Plan limit reached."}</strong> {planLimit.message}</span>
            <button type="button" className="workspace-plan-limit-action" onClick={() => navigate("/upgrade-plan")}>View plans</button>
            <button type="button" className="workspace-plan-limit-close" aria-label="Dismiss plan limit message" onClick={() => setPlanLimit(null)}><i className="ti ti-x" aria-hidden="true" /></button>
          </div>
        )}
        <main key={location.pathname} className="workspace-content workspace-page"><WorkspaceRouteGuard><Outlet /></WorkspaceRouteGuard></main>
      </div>
    </div>
  );
};

export default FoundationLayout;
